import React, { useCallback, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { RiWalletLine } from "@remixicon/react";
import { getWalletTransactionsPage } from "../../../services/dataService";
import { txnRow } from "../../../utils/studentDetail";
import { sessionsLabel } from "../../../utils/studentPresentation";
import { dateTimeLines, showingLabel } from "../../../utils/kitPresentation";
import { tokens } from "../../../utils/figmaTokens";
import { hasPermission, permissions } from "../../../config/permissions";
import AdjustBalanceDialog from "../../../components/AdjustBalanceDialog/AdjustBalanceDialog";
import { SectionCard, TableCard } from "../../../components/kit/SectionCard";
import StatStrip from "../../../components/kit/StatStrip";
import Pager from "../../../components/kit/Pager";
import { TableMessageRow, TwoLineCell } from "../../../components/kit/TableParts";
import { PAGE_SIZE } from "./TabState";
import { usePagedQuery } from "./usePagedQuery";

// + in the success ink, − in the error ink, a zero or a checkout row in plain text.
const amountColor = (row) =>
  row.negative ? tokens.error.ink : row.amountLabel.startsWith("+") ? tokens.success.ink : tokens.text.primary;

// The balance and the ledger (D35): Date · Type · Description · Amount · Balance
// after, newest first on (userId, createdAt desc), drawn from Figma (047; 10791:50724).
// "Lesson credit overview" totals are backlog (D30) — nothing here sums the ledger.
const WalletTab = () => {
  const { student, refreshStudent, actingUser, userRole } = useOutletContext();
  const uid = student.id;
  const [adjustOpen, setAdjustOpen] = useState(false);

  const fetchPage = useCallback((cursor) => getWalletTransactionsPage(uid, { pageSize: PAGE_SIZE, cursor }), [uid]);
  // Keyed on the balance: an adjustment from this tab or from the header's Adjust
  // balance refreshes the student, the balance moves, and the ledger reloads.
  const ledger = usePagedQuery(fetchPage, student.WalletBalance, "Failed to load the wallet.");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Figma's two top cards share a row, 32 px apart; the second (Lesson credit overview) is on the Later list.
          047 D71: until it is built the Wallet card spans the row — return to two columns (md: repeat(2, minmax(0, 1fr))) then. */}
      <SectionCard title="Wallet" Icon={RiWalletLine} action={student.hasFreeSession && <Chip label="Free session unused" />}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <StatStrip items={[{ key: "balance", label: "Current balance", value: sessionsLabel(student.WalletBalance) }]} />
          </Box>
          {hasPermission(userRole, permissions.ADJUST_WALLET) && (
            <Button size="large" variant="outlined" startIcon={<RiWalletLine size={20} />} onClick={() => setAdjustOpen(true)}>
              Adjust balance
            </Button>
          )}
        </Box>
      </SectionCard>

      <TableCard
        title="Wallet transactions"
        subtitle="Newest first"
        footer={
          <Pager
            label={ledger.loading || ledger.error ? "" : showingLabel(ledger.page, ledger.rows.length, PAGE_SIZE)}
            page={ledger.page}
            hasMore={ledger.hasMore}
            onPage={ledger.load}
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Balance after</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ledger.loading || ledger.error || ledger.rows.length === 0 ? (
                <TableMessageRow
                  colSpan={5}
                  loading={ledger.loading}
                  error={ledger.error}
                  onRetry={() => ledger.load(ledger.page)}
                  emptyText="No transactions yet."
                />
              ) : (
                ledger.rows.map((t) => {
                  const row = txnRow(t);
                  const when = dateTimeLines(row.when);
                  return (
                    <TableRow key={t.id} hover>
                      <TableCell>
                        <TwoLineCell top={when ? when.date : "-"} bottom={when?.time} />
                      </TableCell>
                      <TableCell>{row.type}</TableCell>
                      <TableCell>{row.description || "-"}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ whiteSpace: "nowrap", fontSize: 12, lineHeight: "16px", fontWeight: 500, color: amountColor(row) }}
                      >
                        {row.amountLabel}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {row.balanceAfter}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <AdjustBalanceDialog
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        person={student}
        actingUser={actingUser}
        onAdjusted={refreshStudent}
      />
    </Box>
  );
};

export default WalletTab;
